import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Camera as CameraIcon,
  ChevronDown,
  ChevronUp,
  GripHorizontal,
  RotateCcw,
} from "lucide-react";
import { safeLocal } from "../lib/safeStorage";
import { MIRROR_COACH_POS_KEY } from "../lib/storageKeys";
import { track } from "../lib/analytics";

/**
 * AR mirror coach UI.
 *
 * Two independently-positioned absolute children:
 *  • <DraggableCoachCard /> — the step instructions panel.
 *    Defaults to the top-right, with the full instruction visible.
 *    The user can drag it elsewhere and that deliberate placement is
 *    remembered across steps and future mirror sessions.
 *    Draggable by its grip handle; position persists in localStorage
 *    so a user who's set their preferred spot keeps it across opens.
 *  • <CenterShutter /> — bottom-center capture button. Stays put
 *    because that's where users naturally reach.
 */

const DEFAULT_OFFSET = { x: 0, y: 0 };
const TOP_RIGHT_POS_KEY = `${MIRROR_COACH_POS_KEY}.top-right.v1`;

const CARD_WIDTH = 300; // matches `w-[300px]` below

const _readSavedOffset = () => {
  const raw = safeLocal.getItem(TOP_RIGHT_POS_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.x === "number" &&
      typeof parsed.y === "number"
    ) {
      return parsed;
    }
  } catch {
    /* ignore malformed */
  }
  return null;
};

const DraggableCoachCard = ({
  step,
  steps,
  stepIdx,
  zone,
  doneSet,
  onMarkDone,
  onPrev,
  onNext,
  onFinish,
}) => {
  const [expanded, setExpanded] = useState(true);
  // Per-mount offset state so the saved value is only read once on mount
  // (avoids fighting Framer Motion's internal drag math on every render).
  const [offset, setOffset] = useState(
    () => _readSavedOffset() || DEFAULT_OFFSET,
  );
  const constraintsRef = useRef(null);

  // The `key` lets us reset position to default with a single state bump.
  const [resetKey, setResetKey] = useState(0);

  // Clamp the persisted offset against the *current* viewport on mount.
  // Top-right anchor: x moves left (negative), y moves down
  // (positive). Clamp a desktop-saved position on smaller screens.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cardW = Math.min(CARD_WIDTH, Math.max(220, vw - 32));
    const minX = -(vw - cardW - 16);
    const maxX = 0;
    const minY = 0;
    const maxY = Math.max(0, vh - 220);
    const clamped = {
      x: Math.max(minX, Math.min(maxX, offset.x)),
      y: Math.max(minY, Math.min(maxY, offset.y)),
    };
    if (clamped.x !== offset.x || clamped.y !== offset.y) {
      setOffset(clamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = (_e, info) => {
    const next = {
      x: offset.x + info.offset.x,
      y: offset.y + info.offset.y,
    };
    setOffset(next);
    safeLocal.setItem(TOP_RIGHT_POS_KEY, JSON.stringify(next));

    // Fire analytics: which quadrant of the screen did the user park
    // the card in? Helps us learn the right default position from
    // real usage instead of guessing. We use the card's center point.
    try {
      const vw = typeof window !== "undefined" ? window.innerWidth : 0;
      const vh = typeof window !== "undefined" ? window.innerHeight : 0;
      const cardH = expanded ? 240 : 80;
      const centerX = vw - 16 - CARD_WIDTH / 2 + next.x;
      const centerY = 76 + cardH / 2 + next.y;
      const horiz = centerX < vw / 2 ? "left" : "right";
      const vert = centerY < vh / 2 ? "top" : "bottom";
      track("learn_with_trouve_coach_card_moved", {
        quadrant: `${vert}-${horiz}`,
      });
    } catch {
      /* analytics must never break drag */
    }
  };

  const resetPosition = () => {
    safeLocal.removeItem(TOP_RIGHT_POS_KEY);
    setOffset(DEFAULT_OFFSET);
    setResetKey((k) => k + 1);
  };

  // Recompute drag bounds against viewport on mount + resize so the card
  // can never end up off-screen on rotate / orientation change.
  const [bounds, setBounds] = useState({
    left: -200,
    right: 0,
    top: 0,
    bottom: 400,
  });
  useEffect(() => {
    const compute = () => {
      const vw = typeof window !== "undefined" ? window.innerWidth : 400;
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      const cardW = Math.min(CARD_WIDTH, Math.max(220, vw - 32));
      setBounds({
        left: -(vw - cardW - 16),
        right: 0,
        top: 0,
        bottom: Math.max(0, vh - 220),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
    };
  }, []);

  return (
    <div
      ref={constraintsRef}
      className="absolute inset-0 pointer-events-none z-10"
    >
      <motion.div
        key={resetKey}
        drag
        dragMomentum={false}
        dragElastic={0.08}
        dragConstraints={bounds}
        initial={{ x: offset.x, y: offset.y }}
        onDragEnd={handleDragEnd}
        // Default position: top-right, immediately below the mirror
        // controls. Framer Motion offsets only change after a drag.
        style={{
          top: "calc(max(env(safe-area-inset-top), 16px) + 60px)",
          right: 16,
          width: "min(300px, calc(100vw - 32px))",
          touchAction: "none",
        }}
        className="absolute pointer-events-auto"
        data-testid={`live-coach-step-${stepIdx}`}
      >
        <div className="rounded-2xl bg-white/8 backdrop-blur-xl border border-white/15 px-4 py-3 text-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
          {/* Drag grip — visually communicates the card is movable */}
          <div className="flex items-center justify-between mb-2">
            <div
              className="flex items-center gap-1.5 text-white/40 cursor-grab active:cursor-grabbing select-none"
              data-testid="live-coach-drag-handle"
            >
              <GripHorizontal className="h-3.5 w-3.5" />
              <span className="text-[9px] uppercase tracking-[0.22em]">
                Drag to move
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                resetPosition();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-[9px] uppercase tracking-[0.18em] text-white/45 hover:text-white/85 inline-flex items-center gap-1 transition-colors"
              data-testid="live-coach-reset-position"
              title="Reset card position"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </button>
          </div>

          {/* Title row — tap to expand the body */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-full flex items-center justify-between gap-3 group"
            aria-expanded={expanded}
            data-testid="live-coach-toggle-expand"
          >
            <div className="text-left min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F2E8E9]/85 mb-0.5">
                Step {stepIdx + 1} of {steps.length} · {zone.label}
              </div>
              <div className="text-base font-medium leading-snug whitespace-normal break-words">
                {step.title}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkDone();
                }}
                onPointerDown={(e) => e.stopPropagation()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onMarkDone();
                  }
                }}
                className={`h-8 w-8 rounded-full flex items-center justify-center transition-all ${
                  doneSet.has(stepIdx)
                    ? "bg-[#D5A8A4] text-[#2C2123]"
                    : "bg-white/15 text-white hover:bg-white/25"
                }`}
                data-testid="live-coach-mark-done"
                aria-label="Mark done"
              >
                <Check className="h-4 w-4" />
              </span>
              <span
                className="h-8 w-8 rounded-full bg-white/10 text-white/80 flex items-center justify-center group-hover:bg-white/20 transition-colors"
                aria-hidden="true"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </span>
            </div>
          </button>

          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
                data-testid="live-coach-step-body"
              >
                <div className="pt-3">
                  <p className="text-sm text-white/85 leading-relaxed mb-1">
                    {step.what}
                  </p>
                  <p className="text-xs text-white/65 leading-relaxed italic">
                    {zone.description}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <button
              onClick={onPrev}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={stepIdx === 0}
              className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white disabled:opacity-30 transition-colors px-2 py-1.5"
              data-testid="live-coach-prev"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            <div
              className="flex items-center gap-1 overflow-x-auto scrollbar-hide max-w-[55%] px-1"
              data-testid="live-coach-progress-dots"
            >
              {steps.map((s, i) => (
                <div
                  key={`${i}-${s?.category || "step"}-${s?.title || ""}`}
                  className={`h-1.5 shrink-0 rounded-full transition-all ${
                    i === stepIdx
                      ? "w-5 bg-[#D5A8A4]"
                      : doneSet.has(i)
                      ? "w-1.5 bg-[#D5A8A4]/60"
                      : "w-1.5 bg-white/30"
                  }`}
                />
              ))}
            </div>
            {stepIdx < steps.length - 1 ? (
              <button
                onClick={onNext}
                onPointerDown={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 bg-[#9B6A6C] text-white rounded-full px-4 py-1.5 text-sm hover:bg-[#805557] transition-all"
                data-testid="live-coach-next"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={onFinish}
                onPointerDown={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 bg-[#D5A8A4] text-[#2C2123] rounded-full px-4 py-1.5 text-sm hover:bg-[#C29694] transition-all"
                data-testid="live-coach-finish"
              >
                Finish
                <Check className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const CenterShutter = ({ onCapture, capturing }) => (
  <div
    className="absolute bottom-0 left-0 right-0 z-10 pb-[max(env(safe-area-inset-bottom),24px)] flex justify-center pointer-events-none"
    data-testid="live-coach-shutter-row"
  >
    <button
      onClick={onCapture}
      disabled={capturing}
      className="pointer-events-auto group h-[72px] w-[72px] rounded-full bg-white/15 backdrop-blur-md border-[3px] border-white/80 hover:border-white shadow-[0_8px_30px_rgba(0,0,0,0.35)] hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
      data-testid="live-coach-snapshot-btn"
      aria-label="Save this moment to my glow journal"
      title="Save this moment"
    >
      <span className="h-[54px] w-[54px] rounded-full bg-white group-hover:bg-[#FFF6F2] group-active:bg-[#F2E8E9] transition-colors flex items-center justify-center">
        <CameraIcon className="h-5 w-5 text-[#9B6A6C] opacity-0 group-hover:opacity-100 transition-opacity" />
      </span>
    </button>
  </div>
);

const LiveCoachStepCard = ({
  step,
  steps,
  stepIdx,
  zone,
  doneSet,
  onMarkDone,
  onPrev,
  onNext,
  onFinish,
  onCapture,
  capturing,
  showShutter,
}) => {
  if (!step) return null;
  return (
    <>
      <DraggableCoachCard
        step={step}
        steps={steps}
        stepIdx={stepIdx}
        zone={zone}
        doneSet={doneSet}
        onMarkDone={onMarkDone}
        onPrev={onPrev}
        onNext={onNext}
        onFinish={onFinish}
      />
      {showShutter && (
        <CenterShutter onCapture={onCapture} capturing={capturing} />
      )}
    </>
  );
};

export default LiveCoachStepCard;
